import React from 'react';
import { 
  SimpleGrid, 
  Text, 
  Button, 
  HStack, 
  Box, 
  useColorModeValue,
  VStack,
  Flex,
  useBreakpointValue,
  Center,
  Icon,
  Tooltip,
  IconButton
} from '@chakra-ui/react';
import { CaretLeft, CaretRight, ImageSquare, DotsThree } from '@phosphor-icons/react';
import MediaThumbnail from './MediaThumbnail';

export default function MediaGalleryGrid({ 
  media, 
  onMediaClick, 
  showThumbnailTags,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  onTagClick,
  activeTag
}) {
  const totalPages = Math.ceil((media?.length || 0) / itemsPerPage);
  
  // Optimized layout for larger images - more columns for better grid viewing
  const columns = useBreakpointValue({ 
    base: 1,    // 1 column on mobile for large photo viewing
    sm: 2,      // 2 columns on small screens
    md: 3,      // 3 columns on medium screens
    lg: 4,      // 4 columns on large screens
    xl: 5,      // 5 columns on extra large screens
    "2xl": 6    // 6 columns on very large screens
  }) || 3;
  const spacing = useBreakpointValue({ base: 3, sm: 4, md: 4, lg: 5 }) || 4; // More compact spacing for better grid layout
  const buttonSize = useBreakpointValue({ base: "lg", md: "lg" }) || "lg"; // Larger for better mobile touch
  const iconSize = useBreakpointValue({ base: 22, md: 24 }) || 22; // Larger icons for mobile
  
  // Adventure Journal theme colors - using proper theme tokens
  const buttonBg = useColorModeValue('#E6DAC0', '#38291E'); // bg.surface
  const buttonBorder = useColorModeValue('#9C826B', '#6A4E33'); // border.default
  const buttonHoverBg = useColorModeValue('#D9CAA7', '#483123'); // bg.accent
  const activePaginationBg = useColorModeValue('#C19434', '#D6AD60'); // interactive.primary (gold)
  const activePaginationColor = useColorModeValue('white', '#2C231B'); // High contrast
  const textColor = useColorModeValue('#3B2E1E', '#E6D7B8'); // text.default
  const secondaryTextColor = useColorModeValue('#6B573B', '#B7A58B'); // text.muted
  const emptyStateBg = useColorModeValue('#E6DAC0', '#38291E'); // bg.surface
  const emptyStateBorder = useColorModeValue('#9C826B', '#6A4E33'); // border.default
  
  if (!media || media.length === 0) {
    return (
      <Center py={12}>
        <VStack 
          spacing={6} 
          py={10} 
          px={8}
          maxW="sm"
          textAlign="center"
          borderWidth="1px"
          borderColor={emptyStateBorder}
          borderRadius="xl"
          bg={emptyStateBg}
          boxShadow="md"
        >
          <Icon 
            as={ImageSquare} 
            boxSize={16} 
            color={secondaryTextColor} 
            weight="thin" 
          />
          <VStack spacing={2}>
            <Text 
              color={textColor} 
              fontSize="xl" 
              fontWeight="semibold"
              fontFamily="Lora"
            >
              No media uploaded yet
            </Text>
            <Text 
              color={secondaryTextColor} 
              fontSize="md"
              fontFamily="Inter"
              lineHeight="1.5"
            >
              Upload images, videos, or articles to document this archaeological site
            </Text>
          </VStack>
        </VStack>
      </Center>
    );
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of grid
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const paginatedMedia = media?.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // Calculate visible page numbers
  const getVisiblePageNumbers = () => {
    const delta = 1; // Number of pages to show on each side of current page
    let pages = [];
    
    // Always include first page
    pages.push(0);
    
    // Calculate range around current page
    for (let i = Math.max(1, currentPage - delta); i <= Math.min(totalPages - 2, currentPage + delta); i++) {
      pages.push(i);
    }
    
    // Always include last page if there's more than one page
    if (totalPages > 1) {
      pages.push(totalPages - 1);
    }
    
    // Add ellipsis indicators
    let result = [];
    let prev = -1;
    
    for (let i of pages) {
      if (prev >= 0 && i > prev + 1) {
        result.push(-1); // -1 represents ellipsis
      }
      result.push(i);
      prev = i;
    }
    
    return result;
  };

  const visiblePages = getVisiblePageNumbers();

  return (
    <Box className="media-gallery-grid">
      <SimpleGrid 
        columns={columns}
        spacing={spacing}
        mb={8}
        justifyItems="center"
        w="100%"
        px={{ base: 2, md: 0 }} // Add padding on mobile
      >
        {paginatedMedia?.map((item, i) => (
          <MediaThumbnail
            key={item.id || item.url}
            media={item}
            index={i}
            onClick={() => onMediaClick(currentPage * itemsPerPage + i)}
            showThumbnailTags={showThumbnailTags}
            onTagClick={onTagClick}
            activeTag={activeTag}
          />
        ))}
      </SimpleGrid>

      {/* Enhanced Mobile-Friendly Pagination */}
      {totalPages > 1 && (
        <Box>
          {/* Mobile-first pagination info */}
          <Flex justify="center" mb={4}>
            <Text 
              fontSize="sm" 
              color={secondaryTextColor}
              fontFamily="Inter"
              textAlign="center"
            >
              Page {currentPage + 1} of {totalPages} • {media.length} total items
            </Text>
          </Flex>
          
          <Flex 
            justify="center" 
            align="center"
            flexWrap="wrap"
            gap={2}
          >
            {/* Previous Button - Enhanced for mobile */}
            <Tooltip label="Previous page" placement="top">
              <IconButton
                size={buttonSize}
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 0}
                icon={<Icon as={CaretLeft} weight="bold" boxSize={iconSize} />}
                bg={buttonBg}
                borderWidth="1px"
                borderColor={buttonBorder}
                color={textColor}
                _hover={{ 
                  bg: buttonHoverBg,
                  transform: 'translateY(-1px)',
                  boxShadow: 'md'
                }}
                _active={{ transform: 'translateY(0)' }}
                _disabled={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  _hover: { transform: 'none', boxShadow: 'none' }
                }}
                aria-label="Previous page"
                minW="44px" // Touch target
                minH="44px"
                borderRadius="full"
                transition="all 0.2s ease-in-out"
              />
            </Tooltip>
            
            {/* Page Numbers - Optimized for mobile */}
            <HStack spacing={1} mx={2}>
              {visiblePages.map((pageIndex, i) => 
                pageIndex === -1 ? (
                  <Icon 
                    key={`ellipsis-${i}`} 
                    as={DotsThree} 
                    color={secondaryTextColor} 
                    boxSize={6}
                    mx={1}
                  />
                ) : (
                  <Tooltip key={pageIndex} label={`Go to page ${pageIndex + 1}`} placement="top">
                    <Button
                      size={buttonSize}
                      onClick={() => handlePageChange(pageIndex)}
                      bg={currentPage === pageIndex ? activePaginationBg : buttonBg}
                      color={currentPage === pageIndex ? activePaginationColor : textColor}
                      fontWeight={currentPage === pageIndex ? "bold" : "medium"}
                      borderWidth="1px"
                      borderColor={currentPage === pageIndex ? activePaginationBg : buttonBorder}
                      minW="44px" // Touch target
                      minH="44px"
                      borderRadius="full"
                      fontFamily="Inter"
                      fontSize="md"
                      _hover={{
                        bg: currentPage === pageIndex ? activePaginationBg : buttonHoverBg,
                        transform: 'translateY(-1px)',
                        boxShadow: 'md'
                      }}
                      _active={{ transform: 'translateY(0)' }}
                      transition="all 0.2s ease-in-out"
                      aria-label={`Page ${pageIndex + 1}`}
                    >
                      {pageIndex + 1}
                    </Button>
                  </Tooltip>
                )
              )}
            </HStack>

            {/* Next Button - Enhanced for mobile */}
            <Tooltip label="Next page" placement="top">
              <IconButton
                size={buttonSize}
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === totalPages - 1}
                icon={<Icon as={CaretRight} weight="bold" boxSize={iconSize} />}
                bg={buttonBg}
                borderWidth="1px"
                borderColor={buttonBorder}
                color={textColor}
                _hover={{ 
                  bg: buttonHoverBg,
                  transform: 'translateY(-1px)',
                  boxShadow: 'md'
                }}
                _active={{ transform: 'translateY(0)' }}
                _disabled={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  _hover: { transform: 'none', boxShadow: 'none' }
                }}
                aria-label="Next page"
                minW="44px" // Touch target
                minH="44px"
                borderRadius="full"
                transition="all 0.2s ease-in-out"
              />
            </Tooltip>
          </Flex>
        </Box>
      )}
    </Box>
  );
}
